/**
 * FeatureFlagRoute Component
 * Wraps routes behind a LaunchDarkly feature flag.
 * Shows a spinner while the LD client initializes, then redirects to the
 * fallback path if the flag is off. `fallbackPath` may include `:param`
 * segments (e.g. `/my-toolboxes/:topicId`); they are filled from the current
 * route’s `useParams()`.
 *
 * @example
 * <FeatureFlagRoute flag={FeatureFlag.CONTENT_DELETION}>
 *   <ProtectedRoute permission={PermissionEnum.CONTENT_DELETE}>
 *     <ContentDeletePage />
 *   </ProtectedRoute>
 * </FeatureFlagRoute>
 */

import { type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import Spinner from "../componentLibrary/Spinner";
import type { FeatureFlagKey } from "../constants/featureFlags";
import {
  useFeatureFlag,
  useFeatureFlags,
} from "../contexts/FeatureFlagContext";

/** Replace `:paramName` segments with values from `params` (React Router style). */
function interpolateRouteParams(
  path: string,
  params: Readonly<Record<string, string | undefined>>,
): string {
  return path.replace(/:([a-zA-Z]+)/g, (_, key: string) => params[key] ?? "");
}

interface FeatureFlagRouteProps {
  children: ReactNode;
  flag: FeatureFlagKey;
  /** Static path, or use `:topicId`-style segments filled from `useParams()`. */
  fallbackPath?: string;
}

export function FeatureFlagRoute({
  children,
  flag,
  fallbackPath = "/error",
}: FeatureFlagRouteProps) {
  const params = useParams();
  const { isReady } = useFeatureFlags();
  const isEnabled = useFeatureFlag(flag);

  const resolvedFallback = interpolateRouteParams(fallbackPath, params);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="mx-auto" />
      </div>
    );
  }

  if (!isEnabled) {
    return <Navigate to={resolvedFallback} replace />;
  }

  return <>{children}</>;
}
